import { useEffect, useRef } from "react";
import { format } from "date-fns";

import InputFieldWithValidation, { IInputFieldWithValidationProps } from "components/InputFieldWithValidation";
import InputFieldLabel from "components/InputFieldComponents/InputFieldLabel";
import { PaymentSourceInputName } from "v2/dataroutes/BillingData";


export default function CardExpiryInput({ name, validate = true, ...props }: { name?: string, validate?: boolean } & IInputFieldWithValidationProps) {

  const expiryYearRef = useRef<HTMLInputElement>(null);
  const expiryFullYearRef = useRef<HTMLInputElement>(null);
  const expiryMonthRef = useRef<HTMLInputElement>(null);

  const toFullYear = (twoDigitYear: string) => format(new Date(), "yyyy").slice(0, 2) + twoDigitYear;

  // Sync hidden inputs from the initial value after mount, since formatValue
  // may run before the refs are attached to the DOM
  useEffect(() => {
    if (props.value && typeof props.value === "string") {
      const digits = props.value.replace(/\D/g, "");
      if (expiryMonthRef.current) {
        expiryMonthRef.current.value = digits.slice(0, 2);
      }
      if (expiryYearRef.current) {
        expiryYearRef.current.value = digits.slice(2, 4);
      }
      if (expiryFullYearRef.current) {
        expiryFullYearRef.current.value = toFullYear(digits.slice(2, 4));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unformatCardExpiry = (cardExpiryStr: string, cursorPosition: number | null): [ string, number ] => {
    const nonNullCursorPosition = cursorPosition || 0;
    const valueBeforeCursor = cardExpiryStr.slice(0, nonNullCursorPosition);
    const numbersBeforeCursor = valueBeforeCursor.replace(/\D/g, "");
    const realCursorPosition = nonNullCursorPosition - (valueBeforeCursor.length - numbersBeforeCursor.length);

    return [ (numbersBeforeCursor + cardExpiryStr.slice(nonNullCursorPosition).replace(/\D/g, "")).slice(0, 4), realCursorPosition ];
  };

  const formatCardExpiry = (cardExpiry: string | ReadonlyArray<string> | number | undefined, cursorPosition?: number | null): [ string, number | null ] => {
    if (typeof cardExpiry !== "string") {
      return [ "", 0 ];
    } else {
      let cursorShift = 0;
      let formattedValue = cardExpiry.slice(0, 2);
      if (expiryMonthRef.current) {
        expiryMonthRef.current.value = formattedValue;
      }
      if (cardExpiry.length > 2) {
        const expiryYear = cardExpiry.slice(2, 4);
        formattedValue += "/" + expiryYear;
        if (expiryYearRef.current) {
          expiryYearRef.current.value = expiryYear;
        }
        if (expiryFullYearRef.current) {
          expiryFullYearRef.current.value = toFullYear(expiryYear);
        }
        if (cursorPosition && (cursorPosition > 2)) {
          cursorShift++;
        }
      }

      return [ formattedValue, cursorPosition ? (cursorPosition + cursorShift) : null ];
    }
  };


  return (
    <>
      <input type="hidden" name={ name ? name + ".year" : PaymentSourceInputName.card.expiry.year } ref={ expiryYearRef } />
      <input type="hidden" name={ name ? name + ".fullYear" : PaymentSourceInputName.card.expiry.fullYear } ref={ expiryFullYearRef } />
      <input type="hidden" name={ name ? name + ".month" : PaymentSourceInputName.card.expiry.month } ref={ expiryMonthRef } />
      <InputFieldWithValidation
        { ...props }
        label={ <InputFieldLabel label="Expiry" required={ validate } /> }
        type="text"
        placeholder="MM/YY"
        formatValue={ formatCardExpiry }
        unformatValue={ unformatCardExpiry }
        required={ validate }
        pattern={ validate ? "\\d{2}/\\d{2}" : undefined }
        patternMismatchErrorMessage="Invalid expiry date"
      />
    </>
  );
};
